import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Home, Search, Briefcase, Wallet as WalletIcon, User, Plus, FileText, ClipboardList, Shield, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user, token, onLogout }) {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [workerProfile, setWorkerProfile] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [propertyAdminDocs, setPropertyAdminDocs] = useState([]);
  const [propertyAdminProfile, setPropertyAdminProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Fetch jobs
      const jobsRes = await axios.get(`${API}/jobs`, config);
      setJobs(jobsRes.data);

      // Fetch conversations
      try {
        const convRes = await axios.get(`${API}/conversations`, config);
        setConversations(convRes.data);
      } catch (error) {
        console.log('No conversations found');
      }

      // Check if user has worker profile
      if (user.role === 'worker' || user.role === 'both') {
        try {
          const profileRes = await axios.get(`${API}/workers`, config);
          const myProfile = profileRes.data.find(p => p.user_id === user.id);
          setWorkerProfile(myProfile);
        } catch (error) {
          console.log('No worker profile found');
        }
      }

      // Property admin: check docs status
      if (user.role === 'property_admin') {
        try {
          const [docsRes, paProfileRes] = await Promise.all([
            axios.get(`${API}/property-admins/my/documents`, config),
            axios.get(`${API}/property-admins/my/profile`, config)
          ]);
          setPropertyAdminDocs(docsRes.data || []);
          setPropertyAdminProfile(paProfileRes.data || null);
        } catch (error) {
          console.log('Property admin data not found');
        }
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };

    const statusLabels = {
      pending: 'Pendiente',
      accepted: 'Aceptado',
      in_progress: 'En Progreso',
      completed: 'Completado',
      cancelled: 'Cancelado'
    };

    return (
      <Badge className={statusColors[status]}>
        {statusLabels[status]}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#3A4F6C] to-[#2A3B4F] text-white p-6 shadow-lg">
        <div className="container mx-auto">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_emergencia-fix/artifacts/z3hbs9yd_1771467132164.png" 
                  alt="Construclic" 
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-slate-300">Hola, {user.full_name}</p>
              {user.role === 'property_admin' && propertyAdminProfile?.building_name && (
                <p className="text-xs text-slate-400 mt-1" data-testid="property-admin-building-name">
                  {propertyAdminProfile.building_name}
                </p>
              )}
              {user.role === 'property_admin' && (
                <Badge className="bg-[#FF8935] text-white mt-2" data-testid="badge-property-admin">
                  Administrador de Conjunto
                </Badge>
              )}
              {user.role === 'affiliate_admin' && (
                <Badge className="bg-gradient-to-r from-[#FF8935] to-amber-400 text-white mt-2" data-testid="badge-affiliate-admin">
                  Administrador Afiliado · Cashback 5%
                </Badge>
              )}
            </div>
            <Avatar className="h-12 w-12" data-testid="user-avatar">
              <AvatarImage src="" />
              <AvatarFallback className="bg-[#FF8935] text-white font-bold">
                {user.full_name.substring(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </div>

          {/* Role Switcher */}
          {user.role === 'both' && (
            <div className="mt-4 flex gap-2">
              <Badge className="bg-[#FF8935] text-white cursor-pointer" data-testid="role-badge-client">
                Vista Cliente
              </Badge>
              <Badge className="bg-[#3A4F6C] text-white cursor-pointer" data-testid="role-badge-worker">
                Vista Trabajador
              </Badge>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        {/* Property Admin Documents Reminder */}
        {user.role === 'property_admin' && propertyAdminDocs.length < 2 && (
          <Card 
            className="mb-6 cursor-pointer hover:shadow-lg border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 to-amber-50"
            onClick={() => navigate('/profile')}
            data-testid="property-admin-docs-reminder"
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-10 w-10 text-yellow-600" />
                <div>
                  <h3 className="font-bold text-slate-800">Completa tu documentación</h3>
                  <p className="text-sm text-slate-600">
                    Sube tu Cédula y el RUT del conjunto/hotel para dar mayor confianza a los trabajadores.
                  </p>
                </div>
              </div>
              <Badge className="bg-yellow-500 text-white flex-shrink-0">Opcional</Badge>
            </CardContent>
          </Card>
        )}

        {/* Admin Access */}
        {user.role === 'admin' && (
          <Card 
            className="mb-6 cursor-pointer hover:shadow-lg border-2 border-[#FF8935] bg-gradient-to-r from-[#FF8935]/10 to-[#FF8935]/5"
            onClick={() => navigate('/admin')}
            data-testid="action-admin-panel"
          >
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Shield className="h-12 w-12 text-[#FF8935]" />
                <div>
                  <h3 className="font-bold text-lg">Panel de Administración</h3>
                  <p className="text-sm text-slate-600">Gestionar plataforma, usuarios y comisiones</p>
                </div>
              </div>
              <Badge className="bg-[#FF8935] text-white">ADMIN</Badge>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* Show "Publicar Trabajo" for clients and property/affiliate admins */}
          {(user.role === 'client' || user.role === 'both' || user.role === 'property_admin' || user.role === 'affiliate_admin') && (
            <Card 
              className="cursor-pointer hover:shadow-lg border-2 border-transparent hover:border-orange-500"
              onClick={() => navigate('/publish-job')}
              data-testid="action-publish-job"
            >
              <CardContent className="p-6 flex flex-col items-center justify-center text-center">
                <FileText className="h-10 w-10 text-orange-500 mb-2" />
                <h3 className="font-bold">Publicar Trabajo</h3>
              </CardContent>
            </Card>
          )}

          {/* Show "Buscar Trabajos" for workers */}
          {(user.role === 'worker' || user.role === 'both') && (
            <Card 
              className="cursor-pointer hover:shadow-lg border-2 border-transparent hover:border-orange-500"
              onClick={() => navigate('/browse-jobs')}
              data-testid="action-browse-jobs"
            >
              <CardContent className="p-6 flex flex-col items-center justify-center text-center">
                <ClipboardList className="h-10 w-10 text-orange-500 mb-2" />
                <h3 className="font-bold">Buscar Trabajos</h3>
              </CardContent>
            </Card>
          )}

          <Card 
            className="cursor-pointer hover:shadow-lg border-2 border-transparent hover:border-orange-500"
            onClick={() => navigate('/workers')}
            data-testid="action-search-workers"
          >
            <CardContent className="p-6 flex flex-col items-center justify-center text-center">
              <Search className="h-10 w-10 text-orange-500 mb-2" />
              <h3 className="font-bold">Buscar Trabajadores</h3>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card 
            className="cursor-pointer hover:shadow-lg border-2 border-transparent hover:border-orange-500"
            onClick={() => navigate('/wallet')}
            data-testid="action-wallet"
          >
            <CardContent className="p-6 flex flex-col items-center justify-center text-center">
              <WalletIcon className="h-10 w-10 text-orange-500 mb-2" />
              <h3 className="font-bold">Mi Billetera</h3>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg border-2 border-transparent hover:border-orange-500 relative"
            onClick={() => navigate('/chats')}
            data-testid="action-chats"
          >
            <CardContent className="p-6 flex flex-col items-center justify-center text-center">
              <MessageCircle className="h-10 w-10 text-orange-500 mb-2" />
              <h3 className="font-bold">Mis Chats</h3>
              {conversations.filter(c => c.unread_count > 0).length > 0 && (
                <Badge className="absolute top-2 right-2 bg-red-500 text-white h-6 w-6 p-0 flex items-center justify-center rounded-full">
                  {conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0)}
                </Badge>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card 
            className="cursor-pointer hover:shadow-lg border-2 border-transparent hover:border-orange-500"
            onClick={() => navigate('/profile')}
            data-testid="action-profile"
          >
            <CardContent className="p-6 flex flex-col items-center justify-center text-center">
              <User className="h-10 w-10 text-orange-500 mb-2" />
              <h3 className="font-bold">Mi Perfil</h3>
            </CardContent>
          </Card>
        </div>

        {/* Worker Profile Card */}
        {(user.role === 'worker' || user.role === 'both') && !workerProfile && (
          <Card className="mb-6 border-orange-200 bg-orange-50" data-testid="create-profile-card">
            <CardHeader>
              <CardTitle className="text-lg">Crea tu perfil de trabajador</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Completa tu perfil para empezar a recibir solicitudes de trabajo
              </p>
              <Button
                data-testid="create-profile-button"
                onClick={() => navigate('/profile')}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <Plus className="mr-2 h-4 w-4" /> Crear Perfil
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Recent Jobs */}
        <Card data-testid="recent-jobs-card">
          <CardHeader>
            <CardTitle>Mis Trabajos Recientes</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-slate-500">Cargando...</p>
            ) : jobs.length === 0 ? (
              <div className="text-center py-8">
                <Briefcase className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No tienes trabajos aún</p>
                <Button
                  data-testid="start-search-button"
                  onClick={() => navigate('/workers')}
                  className="mt-4 bg-orange-500 hover:bg-orange-600 text-white"
                >
                  Buscar Trabajadores
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {jobs.slice(0, 5).map((job) => (
                  <div
                    key={job.id}
                    data-testid={`job-${job.id}`}
                    onClick={() => navigate(`/job/${job.id}`)}
                    className="p-4 border border-slate-200 rounded-lg hover:border-orange-500 cursor-pointer"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-bold text-lg">{job.title}</h3>
                      {getStatusBadge(job.status)}
                    </div>
                    <p className="text-slate-600 text-sm mb-2">{job.description}</p>
                    <div className="flex justify-between items-center">
                      <Badge className="bg-slate-100 text-slate-700">{job.category}</Badge>
                      {job.budget && (
                        <span className="text-orange-600 font-bold">${job.budget.toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Floating Action Button */}
      <button
        data-testid="fab-publish-job"
        onClick={() => navigate('/publish-job')}
        className="fixed bottom-24 right-6 bg-orange-500 hover:bg-orange-600 text-white p-4 rounded-full shadow-2xl z-40 transition-transform hover:scale-110 active:scale-95"
      >
        <Plus className="h-8 w-8" />
      </button>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 flex justify-around items-center safe-area-pb z-50" data-testid="bottom-nav">
        <button
          data-testid="nav-home"
          onClick={() => navigate('/dashboard')}
          className="flex flex-col items-center text-orange-500"
        >
          <Home className="h-6 w-6" />
          <span className="text-xs mt-1 font-medium">Inicio</span>
        </button>
        <button
          data-testid="nav-search"
          onClick={() => navigate('/workers')}
          className="flex flex-col items-center text-slate-400 hover:text-orange-500"
        >
          <Search className="h-6 w-6" />
          <span className="text-xs mt-1">Buscar</span>
        </button>
        <button
          data-testid="nav-publish"
          onClick={() => navigate('/publish-job')}
          className="flex flex-col items-center text-slate-400 hover:text-orange-500"
        >
          <Plus className="h-6 w-6" />
          <span className="text-xs mt-1">Publicar</span>
        </button>
        <button
          data-testid="nav-wallet"
          onClick={() => navigate('/wallet')}
          className="flex flex-col items-center text-slate-400 hover:text-orange-500"
        >
          <WalletIcon className="h-6 w-6" />
          <span className="text-xs mt-1">Billetera</span>
        </button>
        <button
          data-testid="nav-profile"
          onClick={() => navigate('/profile')}
          className="flex flex-col items-center text-slate-400 hover:text-orange-500"
        >
          <User className="h-6 w-6" />
          <span className="text-xs mt-1">Perfil</span>
        </button>
      </div>
    </div>
  );
}